import { create } from 'zustand';
import type { StaffGroupDto, StaffMemberDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';

/**
 * The team: staff accounts and the permission groups behind them.
 *
 * One store rather than two, because the screens are inseparable — a member row
 * shows its group's name, and a group row shows how many people it would affect.
 * Splitting them would mean two loads that have to agree.
 *
 * The permission catalog is fetched, never hard-coded (ADR-015): a permission
 * added to the API appears here without a release of the admin.
 */

export interface StaffFilters {
  search: string;
  role: '' | 'STAFF' | 'OWNER';
  groupId: string;
  /** '' any, 'true' active, 'false' deactivated. */
  isActive: string;
}

export const EMPTY_STAFF_FILTERS: StaffFilters = {
  search: '',
  role: '',
  groupId: '',
  isActive: '',
};

interface TeamState {
  members: StaffMemberDto[];
  groups: StaffGroupDto[];
  /** The groups' first read, so the tab shows a skeleton rather than "no groups yet". */
  groupsLoading: boolean;
  areas: { area: string; permissions: string[] }[];
  nextCursor: string | null;
  total: number | null;
  loading: boolean;
  loadingMore: boolean;
  saving: boolean;
  error: string | null;
  filters: StaffFilters;

  load: (filters?: Partial<StaffFilters>) => Promise<void>;
  loadMore: () => Promise<void>;
  loadGroups: () => Promise<void>;
  loadPermissions: () => Promise<void>;

  createMember: (input: {
    name: string;
    email: string;
    password: string;
    role?: 'STAFF' | 'OWNER';
    staffGroupId?: string;
    permissions?: string[];
  }) => Promise<boolean>;
  updateMember: (
    id: string,
    input: {
      name?: string;
      role?: 'STAFF' | 'OWNER';
      staffGroupId?: string | null;
      permissions?: string[];
      isActive?: boolean;
    },
  ) => Promise<boolean>;
  setPassword: (id: string, password: string) => Promise<boolean>;

  createGroup: (input: {
    name: string;
    translations?: { en?: { name?: string }; ar?: { name?: string } };
    permissions?: string[];
  }) => Promise<boolean>;
  updateGroup: (
    id: string,
    input: {
      name?: string;
      translations?: { en?: { name?: string }; ar?: { name?: string } };
      permissions?: string[];
      isActive?: boolean;
    },
  ) => Promise<boolean>;
  removeGroup: (id: string) => Promise<boolean>;
  /** A colleague who lost their phone sets two-step sign-in up again (ADR-116). */
  resetTwoFactor: (id: string) => Promise<boolean>;
}

const PAGE_SIZE = 20;

export const useTeamStore = create<TeamState>((set, get) => ({
  members: [],
  groups: [],
  groupsLoading: false,
  areas: [],
  nextCursor: null,
  total: null,
  loading: false,
  loadingMore: false,
  saving: false,
  error: null,
  filters: EMPTY_STAFF_FILTERS,

  async load(filters) {
    const next = { ...get().filters, ...filters };
    set({ loading: true, error: null, filters: next });

    try {
      const page = await withSession(() => adminApi.staff.list(toQuery(next, PAGE_SIZE)));
      set({
        members: page.items,
        nextCursor: page.meta.nextCursor,
        total: page.meta.total ?? null,
        loading: false,
      });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the team.') });
    }
  },

  async loadMore() {
    const { nextCursor, loadingMore, filters, members } = get();
    if (!nextCursor || loadingMore) return;

    set({ loadingMore: true });

    try {
      const page = await withSession(() =>
        adminApi.staff.list({ ...toQuery(filters, PAGE_SIZE), cursor: nextCursor }),
      );
      set({
        members: [...members, ...page.items],
        nextCursor: page.meta.nextCursor,
        loadingMore: false,
      });
    } catch (error) {
      set({ loadingMore: false, error: errorMessage(error, 'Could not load more people.') });
    }
  },

  async loadGroups() {
    set({ groupsLoading: true });
    try {
      const page = await withSession(() => adminApi.staff.groups.list());
      set({ groups: page.items, groupsLoading: false });
    } catch (error) {
      set({ groupsLoading: false, error: errorMessage(error, 'Could not load the groups.') });
    }
  },

  async loadPermissions() {
    try {
      const catalog = await withSession(() => adminApi.staff.permissions());
      set({ areas: catalog.areas });
    } catch (error) {
      set({ error: errorMessage(error, 'Could not load the permission list.') });
    }
  },

  createMember: (input) => mutate(set, get, () => adminApi.staff.create(input)),
  updateMember: (id, input) => mutate(set, get, () => adminApi.staff.update(id, input)),

  setPassword: async (id, password) => {
    set({ saving: true, error: null });

    try {
      await withSession(() => adminApi.staff.setPassword(id, password));
      set({ saving: false });
      return true;
    } catch (error) {
      set({ saving: false, error: errorMessage(error, 'Could not set the password.') });
      return false;
    }
  },

  createGroup: (input) => mutate(set, get, () => adminApi.staff.groups.create(input)),
  updateGroup: (id, input) => mutate(set, get, () => adminApi.staff.groups.update(id, input)),
  removeGroup: (id) => mutate(set, get, () => adminApi.staff.groups.remove(id)),
  resetTwoFactor: (id) => mutate(set, get, () => adminApi.staff.resetTwoFactor(id)),
}));

/**
 * Runs a mutation and reloads both lists.
 *
 * Both, always: changing a group's permissions changes what every member of it
 * can do, and a member list showing yesterday's answer is the kind of thing
 * that gets discovered during an incident.
 */
async function mutate(
  set: (partial: Partial<TeamState>) => void,
  get: () => TeamState,
  call: () => Promise<unknown>,
): Promise<boolean> {
  set({ saving: true, error: null });

  try {
    await withSession(call);
    set({ saving: false });
    await Promise.all([get().load(), get().loadGroups()]);
    return true;
  } catch (error) {
    set({ saving: false, error: errorMessage(error, 'Could not save.') });
    return false;
  }
}

function toQuery(filters: StaffFilters, limit: number) {
  return {
    limit,
    search: filters.search || undefined,
    role: filters.role || undefined,
    staffGroupId: filters.groupId || undefined,
    isActive: filters.isActive === '' ? undefined : filters.isActive === 'true',
  };
}
